import type {
  CompanyResponse,
  ContactResponse,
  EmailTemplatePlaceholder,
  GetVisiblePropertyResponse,
  MemberProfileResponse,
  Prospect,
} from '@immo/api-contract';

export type PlaceholderValueSources = {
  readonly recipient: ContactResponse | null;
  readonly property: GetVisiblePropertyResponse | null;
  readonly prospect: Prospect | null;
  readonly profile: MemberProfileResponse | null;
  readonly mailboxEmail: string | null;
  readonly company: CompanyResponse | null;
};

export type PlaceholderFormat = {
  readonly euro: (amount: number) => string;
  readonly date: (isoDate: string) => string;
  // `pages.workspace.mailboxComposer` translator — the greeting and Anrede are German copy, never code literals.
  readonly t: (key: string, values?: Record<string, string | number>) => string;
};

type Values = Partial<Record<EmailTemplatePlaceholder, string>>;

const present = (value: string | null | undefined): value is string =>
  value !== null && value !== undefined && value.trim() !== '';

function recipientValues(recipient: ContactResponse, t: PlaceholderFormat['t']): Values {
  const person = recipient.contactType === 'PERSON' ? recipient.person : null;

  if (person === null) return {};

  const { firstName, lastName, salutation } = person;
  const greetingKey =
    salutation === 'MRS'
      ? 'template-values.greeting-mrs'
      : salutation === 'MR'
        ? 'template-values.greeting-mr'
        : 'template-values.greeting-name';

  return {
    RECIPIENT_GREETING: t(greetingKey, { firstName, lastName }),
    ...(salutation === 'MRS' ? { RECIPIENT_SALUTATION: t('template-values.salutation-mrs') } : {}),
    ...(salutation === 'MR' ? { RECIPIENT_SALUTATION: t('template-values.salutation-mr') } : {}),
    RECIPIENT_FIRST_NAME: firstName,
    RECIPIENT_LAST_NAME: lastName,
  };
}

function propertyValues(property: GetVisiblePropertyResponse, euro: PlaceholderFormat['euro']): Values {
  const { street, houseNumber, postalCode, city } = property.address;
  const location = [postalCode, city].filter(present).join(' ');
  const sale = property.pricing.sale;
  const price = property.marketingType === 'SALE' && sale !== null && !sale.priceOnInquiry ? sale.purchasePrice : null;

  return {
    PROPERTY_TITLE: property.title,
    ...(present(city) ? { PROPERTY_LOCATION: location } : {}),
    ...(present(street) && present(city)
      ? { PROPERTY_ADDRESS: [[street, houseNumber].filter(present).join(' '), location].join(', ') }
      : {}),
    ...(price !== null ? { PROPERTY_PRICE: euro(price) } : {}),
    PROPERTY_NUMBER: property.objectNumber,
  };
}

function prospectValues(prospect: Prospect, format: PlaceholderFormat): Values {
  return {
    ...(prospect.offeredPrice !== null ? { PROSPECT_OFFER: format.euro(prospect.offeredPrice) } : {}),
    ...(prospect.notaryAppointmentDate !== null
      ? { PROSPECT_NOTARY_APPOINTMENT: format.date(prospect.notaryAppointmentDate) }
      : {}),
  };
}

function senderValues(profile: MemberProfileResponse | null, mailboxEmail: string | null): Values {
  const phone = present(profile?.phone) ? profile.phone : profile?.mobile;
  const email = present(mailboxEmail) ? mailboxEmail : profile?.email;

  return {
    ...(profile !== null ? { SENDER_FIRST_NAME: profile.firstName, SENDER_LAST_NAME: profile.lastName } : {}),
    ...(present(phone) ? { SENDER_PHONE: phone } : {}),
    ...(present(email) ? { SENDER_EMAIL: email } : {}),
  };
}

/** Every placeholder that has a value; a missing key means „stays marked“. */
export function resolvePlaceholderValues(
  sources: PlaceholderValueSources,
  format: PlaceholderFormat,
): Partial<Record<EmailTemplatePlaceholder, string>> {
  const all: Values = {
    ...(sources.recipient !== null ? recipientValues(sources.recipient, format.t) : {}),
    ...(sources.property !== null ? propertyValues(sources.property, format.euro) : {}),
    ...(sources.prospect !== null ? prospectValues(sources.prospect, format) : {}),
    ...senderValues(sources.profile, sources.mailboxEmail),
    ...(sources.company !== null && sources.company.companyName !== null
      ? { COMPANY_NAME: sources.company.companyName }
      : {}),
  };

  return Object.fromEntries(Object.entries(all).filter(([, value]) => present(value)));
}
